import React, { useState, useEffect } from "react"
import { Col, Row, Spinner, Table } from "reactstrap"
import api from "API/Api"
import { usePermissions } from "API/PermissionContext"

const AdvanceSummary = () => {
  const { canExport, MODULE } = usePermissions()
  const MID = MODULE.REPORTS

  const [loading, setLoading] = useState(true)
  const [data, setData] = useState([])
  const [stats, setStats] = useState(null)
  const [tab, setTab] = useState("producer")
  const [sortField, setSortField] = useState("outstanding")
  const [sortAsc, setSortAsc] = useState(false)
  const [search, setSearch] = useState("")

  useEffect(() => { loadData() }, [tab])

  const loadData = async () => {
    setLoading(true)
    try {
      const resp = await api.get("reports/advance-summary", { params: { type: tab } })
      if (resp.data.success) {
        setData(resp.data.data || [])
        setStats(resp.data.stats || null)
      }
    } catch (e) { console.error(e) }
    setLoading(false)
  }

  const exportCSV = () => {
    window.open(`${api.defaults.baseURL}/reports/advance-summary?type=${tab}&export=csv`, "_blank")
  }

  const handleSort = (field) => {
    if (sortField === field) setSortAsc(!sortAsc)
    else { setSortField(field); setSortAsc(false) }
  }

  const filtered = data.filter(d => !search || (d.name || "").toLowerCase().includes(search.toLowerCase()))
  const sorted = [...filtered].sort((a, b) => {
    const av = a[sortField] ?? 0, bv = b[sortField] ?? 0
    return sortAsc ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1)
  })

  const thSort = (label, field) => (
    <th style={{ cursor: "pointer", userSelect: "none" }} onClick={() => handleSort(field)}>
      {label} {sortField === field ? (sortAsc ? "↑" : "↓") : ""}
    </th>
  )

  const cardStyle = (color) => ({
    background: `linear-gradient(135deg, ${color}15, ${color}08)`, borderLeft: `4px solid ${color}`,
    borderRadius: "12px", padding: "20px", boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
  })

  const tabStyle = (t) => ({
    padding: "10px 24px", fontWeight: tab === t ? "700" : "500", color: tab === t ? "#6366f1" : "#64748b",
    borderBottom: tab === t ? "3px solid #6366f1" : "3px solid transparent", cursor: "pointer", fontSize: "14px", transition: "all 0.2s",
  })

  return (
    <div className="dashBody">
      <div className="font-size-18 mb-0 dashHeader">
        <Row>
          <Col md={6}><h4><i className="fas fa-hand-holding-usd me-2" style={{ color: "#6366f1" }}></i>Advance & Loan Summary</h4></Col>
          <Col md={6} style={{ textAlign: "right" }}>
            {canExport(MID) && <button className="btn btn-success btn-sm" onClick={exportCSV}><i className="fas fa-file-csv me-1"></i>Export CSV</button>}
          </Col>
        </Row>
      </div>
      <div className="dashContent">

        {/* Tabs */}
        <div style={{ display: "flex", borderBottom: "1px solid #e2e8f0", marginBottom: "20px" }}>
          <div style={tabStyle("producer")} onClick={() => setTab("producer")}><i className="fas fa-user me-1"></i>Producers</div>
          <div style={tabStyle("vendor")} onClick={() => setTab("vendor")}><i className="fas fa-store me-1"></i>Vendors</div>
        </div>

        {loading ? <div style={{ textAlign: "center", padding: "60px" }}><Spinner color="primary" /></div> : (
          <>
            {/* Stats */}
            {stats && (
              <Row className="mb-4">
                {[
                  { label: `${tab === "producer" ? "Producers" : "Vendors"} with Outstanding`, value: stats.count, color: "#6366f1" },
                  { label: "Total Disbursed", value: "₹" + Number(stats.totalDisbursed).toLocaleString("en-IN", { minimumFractionDigits: 2 }), color: "#0ea5e9" },
                  { label: "Total Recovered", value: "₹" + Number(stats.totalDeducted).toLocaleString("en-IN", { minimumFractionDigits: 2 }), color: "#22c55e" },
                  { label: "Total Outstanding", value: "₹" + Number(stats.totalOutstanding).toLocaleString("en-IN", { minimumFractionDigits: 2 }), color: "#ef4444" },
                ].map((c, i) => (
                  <Col key={i} md={3} sm={6} className="mb-3">
                    <div style={cardStyle(c.color)}>
                      <div style={{ fontSize: "10px", color: "#6c757d", textTransform: "uppercase", letterSpacing: "1px", fontWeight: "600" }}>{c.label}</div>
                      <div style={{ fontSize: "22px", fontWeight: "700", color: "#1e293b", marginTop: "4px" }}>{c.value}</div>
                    </div>
                  </Col>
                ))}
              </Row>
            )}

            {/* Search */}
            <Row className="mb-3">
              <Col md={4}>
                <input type="text" className="form-control form-control-sm" placeholder="Search by name..." value={search} onChange={e => setSearch(e.target.value)} />
              </Col>
              <Col md={8} style={{ textAlign: "right" }}>
                <span style={{ fontSize: "12px", color: "#6c757d" }}>Showing {sorted.length} of {data.length} with outstanding balance</span>
              </Col>
            </Row>

            {/* Table */}
            {sorted.length === 0 ? (
              <div style={{ textAlign: "center", padding: "60px", background: "#fff", borderRadius: "12px" }}>
                <i className="fas fa-check-circle" style={{ fontSize: "48px", color: "#22c55e", marginBottom: "12px" }}></i>
                <p style={{ color: "#059669", fontWeight: "600" }}>No outstanding advances found!</p>
                <p style={{ color: "#94a3b8", fontSize: "13px" }}>All loans and advances have been fully recovered.</p>
              </div>
            ) : (
              <div style={{ background: "#fff", borderRadius: "12px", overflow: "hidden", boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
                <div className="table-responsive">
                  <Table className="table mb-0" size="sm">
                    <thead>
                      <tr style={{ background: "#f8fafc", fontSize: "11px", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                        <th>#</th>
                        <th>No.</th>
                        <th>Name</th>
                        {thSort("Loan", "loan")}
                        {thSort("Cash Advance", "cash_advance")}
                        {thSort("Material Advance", "material_advance")}
                        {thSort("Total Disbursed", "total_disbursed")}
                        {thSort("Total Recovered", "total_deducted")}
                        {thSort("Outstanding", "outstanding")}
                        <th>Recovery %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sorted.map((d, i) => {
                        const pct = d.total_disbursed > 0 ? Math.min(Math.round((d.total_deducted / d.total_disbursed) * 100), 100) : 0
                        return (
                          <tr key={d.id || i} style={{ fontSize: "13px", borderBottom: "1px solid #f1f5f9" }}>
                            <td style={{ color: "#94a3b8" }}>{i + 1}</td>
                            <td><span style={{ background: "#e0e7ff", color: "#4338ca", padding: "2px 8px", borderRadius: "8px", fontSize: "11px", fontWeight: "600" }}>{d.number}</span></td>
                            <td style={{ fontWeight: "600", color: "#1e293b", maxWidth: "180px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.name}</td>
                            <td style={{ color: d.loan > 0 ? "#dc2626" : "#94a3b8" }}>₹{Number(d.loan).toLocaleString("en-IN")}</td>
                            <td style={{ color: d.cash_advance > 0 ? "#ea580c" : "#94a3b8" }}>₹{Number(d.cash_advance).toLocaleString("en-IN")}</td>
                            <td style={{ color: d.material_advance > 0 ? "#7c3aed" : "#94a3b8" }}>₹{Number(d.material_advance).toLocaleString("en-IN")}</td>
                            <td style={{ fontWeight: "600", color: "#0284c7" }}>₹{Number(d.total_disbursed).toLocaleString("en-IN")}</td>
                            <td style={{ color: "#059669" }}>₹{Number(d.total_deducted).toLocaleString("en-IN")}</td>
                            <td style={{ fontWeight: "700", color: "#dc2626", fontSize: "14px" }}>₹{Number(d.outstanding).toLocaleString("en-IN")}</td>
                            <td>
                              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                <div style={{ width: "60px", height: "8px", background: "#f1f5f9", borderRadius: "4px", overflow: "hidden" }}>
                                  <div style={{ width: `${pct}%`, height: "100%", background: pct >= 80 ? "#22c55e" : pct >= 50 ? "#f59e0b" : "#ef4444", transition: "width 0.5s" }}></div>
                                </div>
                                <span style={{ fontSize: "11px", color: "#6c757d", fontWeight: "600" }}>{pct}%</span>
                              </div>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                    <tfoot>
                      <tr style={{ background: "#fef2f2", fontWeight: "700", fontSize: "13px" }}>
                        <td colSpan={3}>TOTAL ({sorted.length})</td>
                        <td style={{ color: "#dc2626" }}>₹{Number(sorted.reduce((s, d) => s + d.loan, 0)).toLocaleString("en-IN")}</td>
                        <td style={{ color: "#ea580c" }}>₹{Number(sorted.reduce((s, d) => s + d.cash_advance, 0)).toLocaleString("en-IN")}</td>
                        <td style={{ color: "#7c3aed" }}>₹{Number(sorted.reduce((s, d) => s + d.material_advance, 0)).toLocaleString("en-IN")}</td>
                        <td style={{ color: "#0284c7" }}>₹{Number(stats?.totalDisbursed || 0).toLocaleString("en-IN")}</td>
                        <td style={{ color: "#059669" }}>₹{Number(stats?.totalDeducted || 0).toLocaleString("en-IN")}</td>
                        <td style={{ color: "#dc2626", fontSize: "14px" }}>₹{Number(stats?.totalOutstanding || 0).toLocaleString("en-IN")}</td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </Table>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default AdvanceSummary
